// Katalog satırının taşma menüsü ⋯ (katalog UX dalgası K2, 2026-11-03): "Kırılıma dönüştür" ve
// "Kullanımdan kaldır" satır sonunda ayrı düğme değil, burada — tablo yan kaydırmadan sığar.
// ARIA menü düğmesi (PackageEditorHeader kalıbı): aria-haspopup/aria-expanded, role=menu +
// menuitem; açılışta odak ilk etkin öğeye, ↑/↓/Home/End `menuKeyAction` ile gezer, Esc kapatır ve
// odağı tetikleyiciye döndürür, Tab kapatır, dışarı tıklama kapatır. Pasif öğe (ör. kategori izni
// yok) `disabled` + `title` nedeni taşır. Tıklama/tuş olayları satıra yayılmaz (satır tıklaması
// düzenleme penceresini açar). Menü kapalıyken öğeler DOM'da `hidden` durur (SSR testleri ve
// e2e `data-testid` seçicileri öğeyi bulur; erişilebilirlik ağacında yoktur). Görünüm altına
// yakın satırda menü YUKARI açılır (son satırların menüsü AppShell `main` dibinde kırpılmasın).

import { useEffect, useId, useRef, useState } from "react";
import { menuKeyAction } from "../../lib/menu-keys";
import { KPI_COPY } from "../../lib/kpi-ui";
import { hitArea44, hitArea44Visual } from "./kpi-form-styles";

export interface CatalogRowMenuItem {
  key: string;
  label: string;
  /** öğenin erişilebilir adı (satır etiketini içerir — e2e seçicisi) */
  ariaLabel: string;
  testId: string;
  onSelect: () => void;
  disabled?: boolean;
  /** pasif öğenin nedeni */
  title?: string | undefined;
  tone?: "danger";
}

export function CatalogRowMenu(props: {
  rowLabel: string;
  items: readonly CatalogRowMenuItem[];
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    rootRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not([disabled])')?.focus();
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (props.items.length === 0) return null;

  const close = (restoreFocus: boolean): void => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  };
  const C = KPI_COPY.catalogRowMenu;
  return (
    <span
      ref={rootRef}
      style={{ position: "relative", display: "inline-block" }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (!open) return;
        const items = [
          ...(rootRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []),
        ];
        const act = menuKeyAction(
          e.key,
          items.indexOf(document.activeElement as HTMLElement),
          items.map((el) => !el.hasAttribute("disabled")),
        );
        if (act === null) return;
        if (act.kind === "focus") {
          e.preventDefault();
          items[act.index]?.focus();
        } else {
          if (act.restoreFocus) e.preventDefault();
          close(act.restoreFocus);
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className="kpi-row-action"
        aria-label={C.aria(props.rowLabel)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        data-testid="kpi-catalog-row-menu"
        style={{ ...hitArea44, margin: 0 }}
        onClick={() => {
          if (!open) setUp(opensUpward(triggerRef.current, props.items.length));
          setOpen((v) => !v);
        }}
      >
        <span
          aria-hidden="true"
          style={{ ...hitArea44Visual, width: 32, padding: 0, color: "var(--color-text-2)" }}
        >
          {C.button}
        </span>
      </button>
      <span
        id={menuId}
        role="menu"
        aria-label={C.aria(props.rowLabel)}
        hidden={!open}
        className="rounded-lg"
        style={{
          position: "absolute",
          right: 0,
          ...(up ? { bottom: "calc(100% + 4px)" } : { top: "calc(100% + 4px)" }),
          zIndex: 20,
          display: open ? "grid" : "none",
          minWidth: 220,
          background: "var(--color-surface)",
          border: "1px solid var(--color-border)",
          boxShadow: "var(--shadow-md, 0 8px 24px rgba(15,42,82,.16))",
          padding: 4,
        }}
      >
        {props.items.map((it) => (
          <button
            key={it.key}
            type="button"
            role="menuitem"
            tabIndex={-1}
            data-testid={it.testId}
            aria-label={it.ariaLabel}
            disabled={it.disabled}
            title={it.title}
            onClick={() => {
              if (it.disabled) return;
              // odak önce tetikleyiciye döner: eylem pencere açarsa pencere kapanınca oraya döner
              close(true);
              it.onSelect();
            }}
            style={{
              textAlign: "left",
              border: 0,
              background: "transparent",
              padding: "8px 12px",
              minHeight: 44,
              borderRadius: 6,
              whiteSpace: "nowrap",
              fontSize: "0.875rem",
              cursor: it.disabled ? "not-allowed" : "pointer",
              opacity: it.disabled ? 0.55 : 1,
              color: it.tone === "danger" ? "var(--color-error)" : "var(--color-text)",
            }}
          >
            {it.label}
          </button>
        ))}
      </span>
    </span>
  );
}

/** Menü (öğe başına 44 px + çerçeve) görünümün altına sığmıyor ve üstte yer varsa yukarı açılır. */
export function opensUpward(trigger: HTMLElement | null, itemCount: number): boolean {
  if (!trigger || typeof window === "undefined") return false;
  const rect = trigger.getBoundingClientRect();
  const menuHeight = itemCount * 44 + 12;
  return rect.bottom + 4 + menuHeight > window.innerHeight && rect.top - 4 - menuHeight >= 0;
}
